import React, { useState } from 'react';
import { useLocation, useParams } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';

import { CATEGORY_PRODUCT_LIST_FILTER_OPTION_MODEL } from '../../../../store/modules/category/models';
import { LIST_PAGINATION_MODEL } from '../../../../store/modules/app/models';
import { asyncSimpleModal } from '../../../../lib/common';
import noticeExcelDownloadResult from '../../../../lib/noticeExcelDownloadResult';
import TableWithSummary from '../../../../components/table/TableWithSummary';
import SubCategoryTagModal from '../modal/SubCategoryTagModal';
import useToastMsg from '../../../../hooks/common/useToastMsg';
import useCategoryProductExcelDownload from '../../../../hooks/queries/category/useCategoryProductExcelDownload';
import useCategoryProductList from '../../../../hooks/queries/category/useCategoryProductList';
import useDeleteCategoryProduct from '../../../../hooks/queries/category/useDeleteCategoryProduct';
import useCategoryProductListRouter from '../../../../hooks/category/useCategoryProductListRouter';
import { CATEGORY } from '../../../../constants/queryKeys';
import categoryToastMsg from '../../../../constants/service/toastMsg/categoryToastMsg';
import { CategoryProductItemV2 } from '../../../../types/category/product';
import columns from './columns';
import './CategoryProductListTab.scss';

const CategoryProductListTab = () => {
  // 2차 카테고리 태그 모달 노출 상태
  const [isOpen, setIsOpen] = useState(false);
  // 2차 카테고리 보기 모달에 노출되는 카테고리 리스트 상태
  const [subCategoryList, setSubCategoryList] = useState([]);
  // 선택한 상품 리스트 상태
  const [checkProductList, setCheckProductList] = useState([]);
  // 상품 삭제 성공 토스트 메세지 노출 여부
  const [isDeleteSuccessToastMsgUsed, setIsDeleteSuccessToastMsgUsed] = useState(false);

  const queryClient = useQueryClient();

  const { search = '' } = useLocation();
  const { id: categoryId } = useParams();
  const { success } = useToastMsg();

  const { products, filterOption, pagination, totalCount } = useCategoryProductList(Number(categoryId), {
    keepPreviousData: true,
    select: (result) => ({ ...result, products: result.products.map((item) => new CategoryProductItemV2(item)) }),
    onSuccess: () => {
      // 상품 삭제 성공 토스트 메세지가 노출되고 있으면 상품 리스트 불러오기 성공 토스트 메세지를 노출하지 않음
      if (isDeleteSuccessToastMsgUsed) return;

      success(categoryToastMsg.GET_CATEGORY_PRODUCT_LIST_REQUEST_SUCCESS);
    },
  });

  const { routeEvent } = useCategoryProductListRouter({ filterOption, pagination });

  const { refetch } = useCategoryProductExcelDownload(Number(categoryId), filterOption, {
    enabled: false,
    onSuccess: (data) => {
      noticeExcelDownloadResult(data.adminEmail);
    },
  });

  const { deleteCategoryProduct } = useDeleteCategoryProduct();

  return (
    <>
      <TableWithSummary
        data={products}
        totalCnt={totalCount}
        initialOption={{
          filterOption: CATEGORY_PRODUCT_LIST_FILTER_OPTION_MODEL,
          pagination: LIST_PAGINATION_MODEL,
        }}
        emptyText="등록 된 상품 정보가 없습니다."
        showPagination
        filterOption={filterOption}
        pagination={pagination}
        getList={routeEvent}
        getExcelDownload={refetch}
        checkedIdList={checkProductList}
        deleteCheckedIdList={async () => {
          const confirmResult = await asyncSimpleModal({
            type: 'confirm',
            title: '상품 삭제',
            message: '상품 삭제 시 해당 카테고리의\nApp/Web에서 노출되지 않을 수 있습니다.\n삭제 하시겠습니까?',
          });

          if (!confirmResult) return;

          deleteCategoryProduct.mutate(
            { categoryId: Number(categoryId), productIds: checkProductList },
            {
              onSuccess: () => {
                // 카테고리 상품 리스트 캐시 데이터 무효화
                queryClient.invalidateQueries([...CATEGORY.PRODUCT_LIST, search]);
                // 상품 삭제 성공 토스트 메세지 노출 상태로 설정
                setIsDeleteSuccessToastMsgUsed(true);
                // 체크 된 상품 리스트 배열 초기화
                setCheckProductList([]);
                // 요청 성공 시, 편집 상태 닫고 성공 토스트 메세지 노출
                success(categoryToastMsg.DELETE_CATEGORY_PRODUCT_REQUEST_SUCCESS);

                // 2초후에 상품 삭제 성공 토스트 메세지 비노출 상태로 설정
                setTimeout(() => {
                  setIsDeleteSuccessToastMsgUsed(false);
                }, 2000);
              },
              onError: (error) => {
                console.error(error);
              },
            },
          );
        }}
        onCheck={(value) => setCheckProductList(value)}
        isUsedCheckbox={true}
        isUsedAllCheck={true}
        columns={(utils) =>
          columns({
            ...utils,
            categoryId: Number(categoryId),
            filterOption,
            pagination,
            routeEvent,
            setIsOpen,
            setSubCategoryList,
          })
        }
      />
      <SubCategoryTagModal isOpen={isOpen} setIsOpen={setIsOpen} subCategoryList={subCategoryList} />
    </>
  );
};

export default CategoryProductListTab;
